import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import offlineStorage, { DownloadedStream, DownloadProgress } from '../services/offlineStorage';
import { logger } from '../utils/logger';
import { Stream } from '../services/streams';

interface OfflineContextType {
  downloadedStreams: DownloadedStream[];
  downloadingStreams: Map<string, DownloadProgress>;
  isOnline: boolean;
  storageUsed: number;
  storageQuota: number;
  isStreamDownloaded: (streamId: string) => boolean;
  downloadStream: (stream: Stream) => Promise<void>;
  deleteDownload: (streamId: string) => Promise<void>;
  getOfflineUrl: (streamId: string) => Promise<string | null>;
  clearAllDownloads: () => Promise<void>;
  refreshDownloads: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

interface OfflineProviderProps {
  children: ReactNode;
}

export const OfflineProvider: React.FC<OfflineProviderProps> = ({ children }) => {
  const [downloadedStreams, setDownloadedStreams] = useState<DownloadedStream[]>([]);
  const [downloadingStreams, setDownloadingStreams] = useState<Map<string, DownloadProgress>>(new Map());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageQuota, setStorageQuota] = useState(0);

  // Initialize offline storage and load downloaded streams
  useEffect(() => {
    initializeOfflineStorage();

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeOfflineStorage = async () => {
    try {
      await offlineStorage.init();
      await refreshDownloads();
      await updateStorageInfo();
    } catch (error) {
      logger.error('Failed to initialize offline storage:', error);
    }
  };

  const refreshDownloads = async () => {
    try {
      const streams = await offlineStorage.getAllDownloadedStreams();
      setDownloadedStreams(streams);
    } catch (error) {
      logger.error('Failed to refresh downloads:', error);
    }
  };

  const updateStorageInfo = async () => {
    try {
      const quota = await offlineStorage.getStorageQuota();
      setStorageUsed(quota.used);
      setStorageQuota(quota.total);
    } catch (error) {
      logger.error('Failed to update storage info:', error);
    }
  };

  const isStreamDownloaded = (streamId: string): boolean => {
    return downloadedStreams.some(s => s.streamId === streamId);
  };

  const downloadStream = async (stream: Stream): Promise<void> => {
    if (isStreamDownloaded(stream.streamId)) {
      throw new Error('Stream already downloaded');
    }

    // Add to downloading map
    setDownloadingStreams(prev => {
      const next = new Map(prev);
      next.set(stream.streamId, {
        streamId: stream.streamId,
        loaded: 0,
        total: 0,
        percentage: 0,
        status: 'downloading'
      });
      return next;
    });

    try {
      await offlineStorage.downloadStream(
        stream.streamId,
        stream.audioUrl,
        {
          name: stream.name,
          description: stream.description,
          genre: stream.genre,
          coverImage: stream.coverImage,
          bitrate: stream.bitrate || 128,
          format: stream.format || 'mp3'
        },
        (progress) => {
          setDownloadingStreams(prev => {
            const next = new Map(prev);
            next.set(stream.streamId, progress);
            return next;
          });
        }
      );

      // Download complete, remove from downloading map and refresh
      setDownloadingStreams(prev => {
        const next = new Map(prev);
        next.delete(stream.streamId);
        return next;
      });

      await refreshDownloads();
      await updateStorageInfo();
    } catch (error) {
      // Download failed, update status
      setDownloadingStreams(prev => {
        const next = new Map(prev);
        const current = next.get(stream.streamId);
        if (current) {
          next.set(stream.streamId, { ...current, status: 'failed' });
        }
        return next;
      });

      // Remove from downloading map after a delay
      setTimeout(() => {
        setDownloadingStreams(prev => {
          const next = new Map(prev);
          next.delete(stream.streamId);
          return next;
        });
      }, 3000);

      throw error;
    }
  };

  const deleteDownload = async (streamId: string): Promise<void> => {
    try {
      await offlineStorage.deleteDownloadedStream(streamId);
      await refreshDownloads();
      await updateStorageInfo();
    } catch (error) {
      logger.error('Failed to delete download:', error);
      throw error;
    }
  };

  const getOfflineUrl = async (streamId: string): Promise<string | null> => {
    try {
      return await offlineStorage.getOfflineStreamUrl(streamId);
    } catch (error) {
      logger.error('Failed to get offline URL:', error);
      return null;
    }
  };

  const clearAllDownloads = async (): Promise<void> => {
    try {
      await offlineStorage.clearAllDownloads();
      await refreshDownloads();
      await updateStorageInfo();
    } catch (error) {
      logger.error('Failed to clear downloads:', error);
      throw error;
    }
  };

  return (
    <OfflineContext.Provider
      value={{
        downloadedStreams,
        downloadingStreams,
        isOnline,
        storageUsed,
        storageQuota,
        isStreamDownloaded,
        downloadStream,
        deleteDownload,
        getOfflineUrl,
        clearAllDownloads,
        refreshDownloads
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};
